import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { CheckIn } from '../../types';
import { exportToCSV } from '../../utils/export';
import { computeSellerStats, fmtKmStat, fmtDuration, fmtBRL, SellerStat } from '../../utils/reportStats';
import { useAccessScope } from '../../hooks/useAccessScope';
import { useAuth } from '../../contexts/AuthContext';
import { fetchCheckinsEscopado } from '../../services/checkinsQuery';
import Pagination, { usePagination } from '../../components/Pagination';

const RESULTADO_LABEL: Record<string, string> = {
  comprador_ausente: 'Comprador Ausente',
  comprador_atendeu: 'Comprador Atendeu',
  comprador_nao_atendeu: 'Comprador Não Atendeu',
  // chaves antigas (visitas registradas antes da mudança)
  realizada: 'Realizada',
  contato_frio: 'Contato frio',
  sem_interesse: 'Sem interesse',
};

const MONTH_NAMES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];

export default function ReportsPage() {
  const { user } = useAuth();
  const scope = useAccessScope();
  const [checkins, setCheckins] = useState<CheckIn[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [detail, setDetail] = useState<SellerStat | null>(null);
  const [printing, setPrinting] = useState(false); // ao imprimir/PDF, renderiza o ranking INTEIRO (não só a página)
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);

  const [yearStr, monStr] = month.split('-');
  const year = Number(yearStr);
  const mon = Number(monStr);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const start = new Date(year, mon - 1, 1, 0, 0, 0, 0);
      const end = new Date(year, mon, 1, 0, 0, 0, 0);
      // try/finally obrigatório: sem ele, uma leitura negada (regra) ou offline deixava o
      // "Carregando..." preso PARA SEMPRE. As telas do app já tinham a guarda; esta ficou de fora.
      try {
        // ESCOPADO: gestor só lê a equipe (regra recusa consulta ampla dele) — ver checkinsQuery.ts
        const raws = await fetchCheckinsEscopado(user, scope, { desde: start, ate: end, ateExclusivo: true, ordem: 'desc', ordenarNoServidor: true });
        setCheckins(raws.map((raw: any) => {
          return {
            ...raw,
            createdAt: raw.createdAt?.toDate?.() ?? new Date(),
            checkoutAt: raw.checkoutAt?.toDate?.() ?? null,
          };
        }) as CheckIn[]);
        setErro(null);
      } catch (e: any) {
        console.warn('Relatórios: falha ao carregar', e);
        setCheckins([]);
        setErro(e?.code === 'permission-denied'
          ? 'Sem permissão para ler estes check-ins. Se você é gestor, sua equipe pode não estar sincronizada.'
          : 'Não foi possível carregar os relatórios. Verifique a conexão.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [year, mon, user, scope]);

  // PDF via window.print(): expande o ranking para TODAS as linhas antes de imprimir (o print imprime
  // o DOM — sem isso o PDF sairia truncado na página atual), e volta à paginação depois.
  useEffect(() => {
    if (!printing) return;
    window.print();
    setPrinting(false);
  }, [printing]);

  // Gestor: só os check-ins dos vendedores da hierarquia dele.
  const monthCheckins = scope.full ? checkins : checkins.filter(c => (scope.sellerIds || []).includes(c.sellerId));
  const stats = computeSellerStats(monthCheckins);
  const monthLabel = `${MONTH_NAMES[mon - 1]} de ${year}`;
  const kmTotal = stats.reduce((acc, s) => acc + s.kmMeters, 0);
  const checkoutsTotal = stats.reduce((acc, s) => acc + s.checkouts, 0);
  const pedidosTotal = stats.reduce((acc, s) => acc + s.pedidos, 0);
  const valorTotal = stats.reduce((acc, s) => acc + s.pedidoValor, 0);

  // Paginação do ranking (25 por página) — a POSIÇÃO continua global (pager.startIdx + i)
  const pager = usePagination(stats, month);
  // Paginação das visitas no modal de detalhe (lista mais longa da tela — pode passar de centenas)
  const visitsPager = usePagination(detail?.visits || [], detail?.sellerId);

  const fmtDateTime = (d: Date) => d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  const handleExportExcel = () => {
    const rows = stats.map((s, i) => ({
      Posicao: i + 1,
      Vendedor: s.name,
      CheckIns: s.total,
      CheckOuts: s.checkouts,
      ClientesUnicos: s.clients,
      KmRodado: (s.kmMeters / 1000).toFixed(1),
      PermanenciaMedia: fmtDuration(s.avgPermanenciaMs),
      Pedidos: s.pedidos,
      ValorPedidos: s.pedidoValor.toFixed(2),
      Orcamentos: s.orcamentos,
      ValorOrcamentos: s.orcamentoValor.toFixed(2),
      ComObservacao: s.withNotes,
    }));
    exportToCSV(rows, `relatorio-${month}`);
  };

  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.title}>Relatórios</h1>
          <p style={styles.subtitle}>{monthLabel}</p>
        </div>
        <div style={styles.headerActions}>
          <input type="month" style={styles.monthPicker} value={month} onChange={e => setMonth(e.target.value)} />
          <button style={styles.exportExcel} onClick={handleExportExcel}>⬇️ Excel</button>
          <button style={styles.exportPdf} onClick={() => setPrinting(true)}>🖨️ PDF</button>
        </div>
      </div>

      {erro && <p style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: 12, borderRadius: 8 }}>{erro}</p>}
      {loading ? <p>Carregando...</p> : (
        <>
          <div style={styles.kpiGrid}>
            <div style={styles.kpi}>
              <div style={styles.kpiValue}>{monthCheckins.length}</div>
              <div style={styles.kpiLabel}>Check-ins no mês</div>
            </div>
            <div style={styles.kpi}>
              <div style={styles.kpiValue}>{checkoutsTotal}</div>
              <div style={styles.kpiLabel}>Check-outs</div>
            </div>
            <div style={styles.kpi}>
              <div style={styles.kpiValue}>{Array.from(new Set(monthCheckins.map(c => c.clientId))).length}</div>
              <div style={styles.kpiLabel}>Clientes visitados</div>
            </div>
            <div style={styles.kpi}>
              <div style={styles.kpiValue}>{fmtKmStat(kmTotal)}</div>
              <div style={styles.kpiLabel}>KM rodado (estim.)</div>
            </div>
            <div style={styles.kpi}>
              <div style={styles.kpiValue}>{pedidosTotal}</div>
              <div style={styles.kpiLabel}>Pedidos</div>
            </div>
            <div style={styles.kpi}>
              <div style={styles.kpiValue}>{fmtBRL(valorTotal)}</div>
              <div style={styles.kpiLabel}>Valor em pedidos</div>
            </div>
          </div>

          <h2 style={styles.sectionTitle}>Ranking de Vendedores</h2>
          <p style={styles.hint}>Clique em um vendedor para ver o detalhamento das visitas.</p>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['#', 'Vendedor', 'Check-ins', 'Check-outs', 'Clientes', 'KM rodado', 'Permanência média', 'Pedidos', 'Valor'].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(printing ? stats : pager.pageItems).map((s, i) => {
                  const pos = printing ? i : pager.startIdx + i; // posição global no ranking (0-based)
                  return (
                  <tr key={s.sellerId} style={styles.trClick} onClick={() => setDetail(s)}>
                    <td style={styles.td}>
                      <span style={{ ...styles.rank, ...(pos === 0 ? styles.gold : pos === 1 ? styles.silver : pos === 2 ? styles.bronze : {}) }}>
                        {pos + 1}
                      </span>
                    </td>
                    <td style={styles.td}><strong>{s.name}</strong></td>
                    <td style={styles.td}>{s.total}</td>
                    <td style={styles.td}>{s.checkouts}</td>
                    <td style={styles.td}>{s.clients}</td>
                    <td style={styles.td}>{fmtKmStat(s.kmMeters)}</td>
                    <td style={styles.td}>{fmtDuration(s.avgPermanenciaMs)}</td>
                    <td style={styles.td}>{s.pedidos}</td>
                    <td style={styles.td}>{s.pedidoValor > 0 ? fmtBRL(s.pedidoValor) : '—'}</td>
                  </tr>
                  );
                })}
                {stats.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: '#999' }}>Nenhum check-in neste mês.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination pager={pager} label="vendedores" />
        </>
      )}

      {detail && (
        <div style={styles.overlay} onClick={() => setDetail(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>{detail.name}</h2>
                <p style={styles.modalSub}>{monthLabel}</p>
              </div>
              <button style={styles.closeBtn} onClick={() => setDetail(null)}>✕</button>
            </div>

            <div style={styles.detailKpis}>
              <div style={styles.detailKpi}><strong>{detail.total}</strong><span>Check-ins</span></div>
              <div style={styles.detailKpi}><strong>{detail.checkouts}</strong><span>Check-outs</span></div>
              <div style={styles.detailKpi}><strong>{fmtKmStat(detail.kmMeters)}</strong><span>KM rodado</span></div>
              <div style={styles.detailKpi}><strong>{fmtDuration(detail.avgPermanenciaMs)}</strong><span>Permanência média</span></div>
              <div style={styles.detailKpi}><strong>{fmtDuration(detail.totalPermanenciaMs || null)}</strong><span>Permanência total</span></div>
              <div style={styles.detailKpi}><strong>{detail.pedidos}</strong><span>Pedidos</span></div>
              <div style={styles.detailKpi}><strong>{fmtBRL(detail.pedidoValor)}</strong><span>Valor em pedidos</span></div>
              <div style={styles.detailKpi}><strong>{detail.orcamentos}</strong><span>Orçamentos</span></div>
              <div style={styles.detailKpi}><strong>{fmtBRL(detail.orcamentoValor)}</strong><span>Valor em orçamentos</span></div>
            </div>

            <div style={styles.detailTableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['Cliente', 'Entrada', 'Saída', 'Permanência', 'Resultado', 'Pedido/Orçamento'].map(h => <th key={h} style={styles.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {visitsPager.pageItems.map((v, i) => (
                    <tr key={visitsPager.startIdx + i} style={styles.tr}>
                      <td style={styles.td}>{v.clientName}</td>
                      <td style={styles.td}>{fmtDateTime(v.checkinAt)}</td>
                      <td style={styles.td}>{v.checkoutAt ? fmtDateTime(v.checkoutAt) : <span style={{ color: '#d97706' }}>Em aberto</span>}</td>
                      <td style={styles.td}>{fmtDuration(v.permanenciaMs)}</td>
                      <td style={styles.td}>{v.resultado ? RESULTADO_LABEL[v.resultado] || v.resultado : '—'}</td>
                      <td style={styles.td}>{v.pedidoStatus === 'orcamento' ? `Orçamento${v.pedidoValor ? ' ' + fmtBRL(v.pedidoValor) : ''}` : v.pedidoFeito ? (v.pedidoValor ? fmtBRL(v.pedidoValor) : 'Sim') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination pager={visitsPager} label="visitas" />
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  subtitle: { color: '#666', margin: '4px 0 0', fontSize: 14 },
  headerActions: { display: 'flex', gap: 10, alignItems: 'center' },
  monthPicker: { border: '1px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14, background: '#fff', cursor: 'pointer' },
  exportExcel: { background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  exportPdf: { background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 32 },
  kpi: { background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', textAlign: 'center' },
  kpiValue: { fontSize: 32, fontWeight: 700, color: '#f5821f' },
  kpiLabel: { fontSize: 13, color: '#666', marginTop: 4 },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 },
  hint: { fontSize: 13, color: '#888', margin: '0 0 14px' },
  tableWrapper: { background: '#fff', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 640 },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', borderBottom: '2px solid #f0f0f0', background: '#fafafa', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f5f5f5' },
  trClick: { borderBottom: '1px solid #f5f5f5', cursor: 'pointer' },
  td: { padding: '12px 16px', fontSize: 14, color: '#333' },
  rank: { display: 'inline-block', width: 28, height: 28, borderRadius: 14, background: '#f0f0f0', color: '#666', textAlign: 'center', lineHeight: '28px', fontWeight: 700, fontSize: 13 },
  gold: { background: '#fef08a', color: '#854d0e' },
  silver: { background: '#e5e7eb', color: '#374151' },
  bronze: { background: '#fed7aa', color: '#7c2d12' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 720, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 24px', borderBottom: '1px solid #f0f0f0' },
  modalTitle: { fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  modalSub: { fontSize: 13, color: '#666', margin: '2px 0 0' },
  closeBtn: { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' },
  detailKpis: { display: 'flex', gap: 12, flexWrap: 'wrap', padding: '16px 24px' },
  detailKpi: { display: 'flex', flexDirection: 'column', background: '#f9fafb', borderRadius: 10, padding: '10px 16px', minWidth: 110 },
  detailTableWrap: { padding: '0 24px 20px', overflowY: 'auto' },
};
